/** Provider identity only. Setup instructions and API adapters remain outside the kit. */
export const PROVIDER_MANIFEST = {
  google_workspace: { label: 'Google Workspace', monogram: 'GW' },
  microsoft365: { label: 'Microsoft 365', monogram: 'M' },
  slack: { label: 'Slack', monogram: 'S' },
  atlassian: { label: 'Atlassian', monogram: 'A' },
  box: { label: 'Box', monogram: 'B' },
  okta: { label: 'Okta', monogram: 'O' },
  zoom: { label: 'Zoom', monogram: 'Z' },
  github_enterprise: { label: 'GitHub Enterprise', monogram: 'GH' },
  aws: { label: 'AWS IAM Identity Center', monogram: 'AWS', setupLabel: 'AWS credentials' },
  custom: { label: 'Custom', monogram: 'C' },
  uptimerobot: { label: 'UptimeRobot', monogram: 'UR' },
  anthropic_api: { label: 'Anthropic Claude', monogram: 'AC' },
  openai_api: { label: 'OpenAI', monogram: 'AI' },
  rippling: { label: 'Rippling', monogram: 'R' },
  inbound_webhook: { label: 'Inbound webhook', monogram: 'IN' },
  outbound_webhook: { label: 'Outbound webhook', monogram: 'OUT' },
  generic_siem: { label: 'SIEM forwarder endpoint', monogram: 'SI' },
} as const;

export type ProviderKey = keyof typeof PROVIDER_MANIFEST;
export type ProviderIdentity = { label: string; monogram: string; setupLabel?: string };

// These are deliberate identity aliases, never inferred from backendProviderType.
// generic_webhook is ambiguous (inbound/outbound) and intentionally has no alias.
export const PROVIDER_ALIASES: Readonly<Record<string, ProviderKey>> = {
  google: 'google_workspace', gsuite: 'google_workspace',
  ms365: 'microsoft365', microsoft_365: 'microsoft365',
  github: 'github_enterprise', aws_iam_identity_center: 'aws',
};

export function providerIdentity(provider?: string | null): ProviderIdentity | undefined {
  if (!provider) return undefined;
  const key = Object.prototype.hasOwnProperty.call(PROVIDER_ALIASES, provider)
    ? PROVIDER_ALIASES[provider]! : provider;
  return Object.prototype.hasOwnProperty.call(PROVIDER_MANIFEST, key)
    ? PROVIDER_MANIFEST[key as ProviderKey] : undefined;
}

// Backend provisioning CHECK constraint; shared by its API compatibility export.
export const PROVIDER_TYPES = [
  'google_workspace', 'microsoft365', 'slack', 'atlassian', 'box',
  'okta', 'zoom', 'github_enterprise', 'aws', 'custom',
] as const;
export type ProviderType = typeof PROVIDER_TYPES[number];
export const PROVIDER_LABELS: Record<ProviderType, string> = Object.fromEntries(
  PROVIDER_TYPES.map((key) => [key, PROVIDER_MANIFEST[key].label]),
) as Record<ProviderType, string>;
